import { getBackendUrl } from "../../lib/utils";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, Upload, AlertCircle, CheckCircle2, ChevronRight, HelpCircle, User } from 'lucide-react';
import * as Lucide from 'lucide-react';
import { getImageUrl } from '../../lib/utils';

const iconOptions = [
  'LandPlot', 'Factory', 'Users', 'Layers', 'Maximize', 'Sparkles', 'Zap', 
  'Globe', 'TrendingUp', 'ShieldCheck', 'Ruler', 'Truck', 'TreePine', 
  'MapPin', 'Building2', 'Play', 'CheckCircle2', 'Leaf', 'Lightbulb'
];

const IconSelect = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleSelect = (iconName) => {
    onChange(iconName);
    setIsOpen(false);
  };
  
  const IconComponent = Lucide[value] || Lucide.HelpCircle;
  
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full bg-white border border-slate-300 rounded-xl text-left font-semibold focus:outline-none flex items-center justify-between shadow-sm hover:border-slate-400 transition ${className || 'px-4 py-2.5 text-sm'}`}
      >
        <span className="flex items-center gap-2">
          <IconComponent size={className?.includes('text-xs') ? 14 : 18} className="text-slate-600" />
          <span className="truncate">{value}</span>
        </span>
        <ChevronRight size={className?.includes('text-xs') ? 12 : 16} className="text-slate-400 transform rotate-90" />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setIsOpen(false)}></div>
          <div className="absolute left-0 mt-1.5 w-60 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-2xl shadow-xl z-40 p-2 grid grid-cols-2 gap-1">
            {iconOptions.map((icon) => {
              const CurrentIcon = Lucide[icon] || Lucide.HelpCircle;
              return (
                <button
                  key={icon}
                  type="button"
                  onClick={() => handleSelect(icon)}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-xl text-[11px] font-bold text-left transition ${
                    value === icon
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <CurrentIcon size={13} className={value === icon ? 'text-blue-500' : 'text-slate-400'} />
                  <span className="truncate">{icon}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const Avatar = ({ src, alt, size = 'md', accent = 'blue' }) => {
  const sizeClasses = {
    sm: 'w-16 h-16 border-2',
    md: 'w-24 h-24 border-[3px]',
    lg: 'w-32 h-32 border-4',
    xl: 'w-36 h-36 border-4',
  };
  const accentClasses = {
    blue: 'border-blue-100',
    amber: 'border-amber-100',
    emerald: 'border-emerald-100',
    slate: 'border-slate-200',
  };
  
  return (
    <div className={`rounded-full overflow-hidden mb-4 bg-slate-100 flex items-center justify-center ${sizeClasses[size] || sizeClasses.md} ${accentClasses[accent] || accentClasses.blue}`}>
      {src ? (
        <img src={getImageUrl(src)} alt={alt} className="w-full h-full object-cover object-center" />
      ) : (
        <User className="text-slate-300" size={size === 'sm' ? 24 : 36} strokeWidth={1.5} />
      )}
    </div>
  );
};

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('slides');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // CMS settings state
  const [slides, setSlides] = useState([]);
  const [stats, setStats] = useState([]);
  const [statsSection, setStatsSection] = useState({});
  const [homeAbout, setHomeAbout] = useState({});
  const [aboutPage, setAboutPage] = useState({});
  const [missionVision, setMissionVision] = useState({});
  const [qualityPolicy, setQualityPolicy] = useState({});
  const [boardMembers, setBoardMembers] = useState({});
  const [projectDetail, setProjectDetail] = useState({});
  const [hrPage, setHrPage] = useState({});
  const [contactSettings, setContactSettings] = useState({});
  const [footerSettings, setFooterSettings] = useState({});
  const [homeParallax, setHomeParallax] = useState({
    bg_image: "/images/renders/1.jpg",
    subtitle: "GELECEĞİN VİZYONU",
    title: "Trakya'nın En Büyük \n Karma Sanayi Sitesi"
  });
  const [smtpSettings, setSmtpSettings] = useState({});
  const [recaptchaSettings, setRecaptchaSettings] = useState({});
  const [headerScripts, setHeaderScripts] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      
      if (res.data.home_hero_slides) setSlides(res.data.home_hero_slides);
      if (res.data.home_stats) setStats(res.data.home_stats);
      if (res.data.home_stats_section) setStatsSection(res.data.home_stats_section);
      if (res.data.home_parallax) setHomeParallax(res.data.home_parallax);
      if (res.data.home_about) setHomeAbout(res.data.home_about);
      if (res.data.about_page) setAboutPage(res.data.about_page);
      if (res.data.mission_vision) setMissionVision(res.data.mission_vision);
      if (res.data.quality_policy) setQualityPolicy(res.data.quality_policy);
      if (res.data.board_members) setBoardMembers(res.data.board_members);
      if (res.data.smtp_settings) setSmtpSettings(res.data.smtp_settings);
      if (res.data.recaptcha_settings) setRecaptchaSettings(res.data.recaptcha_settings);
      if (res.data.project_detail) setProjectDetail(res.data.project_detail);
      if (res.data.hr_page) setHrPage(res.data.hr_page);
      if (res.data.contact_settings) setContactSettings(res.data.contact_settings);
      if (res.data.footer_settings) setFooterSettings(res.data.footer_settings);
      if (res.data.header_scripts) setHeaderScripts(res.data.header_scripts);
    } catch (err) {
      console.error("Error loading settings", err);
      setError("Ayarlar yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const saveSettingKey = async (keyName, valueData) => {
    setLoading(true);
    setSuccess('');
    setError('');
    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: keyName,
        value_data: valueData
      }, { withCredentials: true });
      
      setSuccess("Değişiklikler başarıyla kaydedildi!");
      setTimeout(() => setSuccess(''), 3000);
      fetchSettings();
    } catch (err) {
      console.error("Error saving setting", err);
      setError("Ayarlar kaydedilirken hata oluştu: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, onUploadSuccess) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.post(`${backendUrl}/api/settings/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      onUploadSuccess(res.data.image_url);
    } catch (err) {
      console.error("Image upload error", err);
      setError("Resim yükleme başarısız oldu.");
    } finally {
      setLoading(false);
    }
  };

  // --- Tab 1: Hero Slides Handlers ---
  const handleAddSlide = () => {
    const newSlide = {
      id: Date.now(),
      image: '/images/renders_hero/hero1.jpg',
      subtitle: 'YENİ SLAYT ALT BAŞLIĞI',
      title: 'Yeni Slayt Başlığı',
      desc: 'Slayt açıklaması buraya gelecek.',
      link: '/proje',
      link_text: 'Projeyi İncele',
      video_link: 'https://www.youtube.com/watch?v=Gsw55Ju4kiQ',
      video_text: 'Tanıtım Filmi'
    };
    setSlides([...slides, newSlide]);
  };

  const handleRemoveSlide = (id) => {
    setSlides(slides.filter(s => s.id !== id));
  };

  const handleSlideChange = (id, field, value) => {
    setSlides(slides.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  // --- Tab 2: Stats & Home About Preview Handlers ---
  const handleStatChange = (index, field, value) => {
    const updated = [...stats];
    updated[index][field] = value;
    setStats(updated);
  };

  const handleHomeAboutPointChange = (index, field, value) => {
    const updatedPoints = [...homeAbout.points];
    updatedPoints[index][field] = value;
    setHomeAbout({ ...homeAbout, points: updatedPoints });
  };

  const handleAddHomeAboutPoint = () => {
    const updatedPoints = [...(homeAbout.points || []), { icon: 'Maximize', title: '', desc: '' }];
    setHomeAbout({ ...homeAbout, points: updatedPoints });
  };

  const handleRemoveHomeAboutPoint = (index) => {
    const updatedPoints = homeAbout.points.filter((_, idx) => idx !== index);
    setHomeAbout({ ...homeAbout, points: updatedPoints });
  };

  // --- Tab 3: About Page Handlers ---
  const handleAboutParagraphChange = (index, value) => {
    const updated = [...aboutPage.paragraphs];
    updated[index] = value;
    setAboutPage({ ...aboutPage, paragraphs: updated });
  };

  const handleAddAboutParagraph = () => {
    setAboutPage({ ...aboutPage, paragraphs: [...(aboutPage.paragraphs || []), ''] });
  };

  const handleRemoveAboutParagraph = (index) => {
    setAboutPage({ ...aboutPage, paragraphs: aboutPage.paragraphs.filter((_, idx) => idx !== index) });
  };

  // --- Tab 4: Mission Vision Handlers ---
  const handleMissionValueChange = (index, field, value) => {
    const updated = [...missionVision.values];
    updated[index][field] = value;
    setMissionVision({ ...missionVision, values: updated });
  };

  // --- Tab 5: Quality Policy Handlers ---
  const handlePolicyChange = (index, field, value) => {
    const updated = [...qualityPolicy.policies];
    updated[index][field] = value;
    setQualityPolicy({ ...qualityPolicy, policies: updated });
  };

  // --- Tab 6: Board Members Handlers ---
  const handleBoardMemberChange = (index, field, value) => {
    const updated = [...boardMembers.boardMembers];
    updated[index][field] = value;
    setBoardMembers({ ...boardMembers, boardMembers: updated });
  };

  const handleAddBoardMember = () => {
    const updated = [...(boardMembers.boardMembers || []), { role: 'Yönetim Kurulu Üyesi', name: '', image: null }];
    setBoardMembers({ ...boardMembers, boardMembers: updated });
  };

  const handleRemoveBoardMember = (index) => {
    const updated = boardMembers.boardMembers.filter((_, idx) => idx !== index);
    setBoardMembers({ ...boardMembers, boardMembers: updated });
  };

  const handleAuditMemberChange = (index, field, value) => {
    const updated = [...boardMembers.auditCommittee];
    updated[index][field] = value;
    setBoardMembers({ ...boardMembers, auditCommittee: updated });
  };

  const handleAddAuditMember = () => {
    const updated = [...(boardMembers.auditCommittee || []), { role: 'Denetim Kurulu Üyesi', name: '', image: null }];
    setBoardMembers({ ...boardMembers, auditCommittee: updated });
  };

  const handleRemoveAuditMember = (index) => {
    const updated = boardMembers.auditCommittee.filter((_, idx) => idx !== index);
    setBoardMembers({ ...boardMembers, auditCommittee: updated });
  };

  // --- Tab 7: Project Details Handlers ---
  const handleProjStatChange = (index, field, value) => {
    const updated = [...projectDetail.stats];
    updated[index][field] = value;
    setProjectDetail({ ...projectDetail, stats: updated });
  };

  const handleFeatureChange = (index, field, value) => {
    const updated = [...projectDetail.features];
    updated[index][field] = value;
    setProjectDetail({ ...projectDetail, features: updated });
  };

  const handleKrokiChange = (index, field, value) => {
    const updated = [...projectDetail.krokiler];
    updated[index][field] = value;
    setProjectDetail({ ...projectDetail, krokiler: updated });
  };

  const handleRemoveGalleryRender = (index) => {
    const updated = projectDetail.gallery.filter((_, idx) => idx !== index);
    setProjectDetail({ ...projectDetail, gallery: updated });
  };

  const handleAddGalleryRender = (url) => {
    const updated = [...(projectDetail.gallery || []), url];
    setProjectDetail({ ...projectDetail, gallery: updated });
  };

  const handleBenefitChange = (index, value) => {
    const updated = [...hrPage.benefits];
    updated[index] = value;
    setHrPage({ ...hrPage, benefits: updated });
  };

  const handleAddBenefit = () => {
    setHrPage({ ...hrPage, benefits: [...(hrPage.benefits || []), ''] });
  };

  const handleRemoveBenefit = (index) => {
    setHrPage({ ...hrPage, benefits: hrPage.benefits.filter((_, idx) => idx !== index) });
  };

  const handlePositionChange = (index, value) => {
    const updated = [...hrPage.positions];
    updated[index] = value;
    setHrPage({ ...hrPage, positions: updated });
  };

  const handleAddPosition = () => {
    setHrPage({ ...hrPage, positions: [...(hrPage.positions || []), ''] });
  };

  const handleRemovePosition = (index) => {
    setHrPage({ ...hrPage, positions: hrPage.positions.filter((_, idx) => idx !== index) });
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200/80 p-6 md:p-8 shadow-sm">
      {/* Toast Alert */}
      {(success || error) && (
        <div className="fixed top-24 right-8 z-50 animate-bounce">
          {success && (
            <div className="bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <CheckCircle2 size={24} className="text-emerald-500" />
              <span className="font-bold text-sm">{success}</span>
            </div>
          )}
          {error && (
            <div className="bg-red-50 text-red-800 border border-red-200 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <AlertCircle size={24} className="text-red-500" />
              <span className="font-bold text-sm">{error}</span>
            </div>
          )}
        </div>
      )}

      {/* Tabs list */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-4 mb-8">
        {[
          { id: 'slides', label: 'Anasayfa Slider' },
          { id: 'home_about', label: 'Anasayfa Sayaç & Hakkımızda' },
          { id: 'about_page', label: 'Hakkımızda Sayfası' },
          { id: 'mission_vision', label: 'Misyon & Vizyon' },
          { id: 'quality_policy', label: 'Kalite Politikası' },
          { id: 'board_members', label: 'Yönetim Kadrosu' },
          { id: 'project_detail', label: 'Proje Detayları' },
          { id: 'hr_page', label: 'İnsan Kaynakları' },
          { id: 'contact_settings', label: 'İletişim Ayarları' },
          { id: 'footer_settings', label: 'Footer Ayarları' },
          { id: 'smtp_settings', label: 'SMTP & Güvenlik Ayarları' },
          { id: 'header_scripts', label: 'Header Kod Ekleme' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSuccess(''); setError(''); }}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all duration-200 ${
              activeTab === tab.id 
                ? 'bg-blue-600 text-white shadow-md shadow-blue-600/10' 
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-blue-600 font-bold mb-4 flex items-center gap-2 text-sm"><HelpCircle className="animate-spin" size={18} /> Yükleniyor / İşleniyor...</div>}

      {/* TAB CONTENTS */}
      
      {/* 1. HERO SLIDES TAB */}
      {activeTab === 'slides' && (
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-extrabold text-slate-800">Anasayfa Manşet Slider Ayarları</h2>
            <button 
              onClick={handleAddSlide}
              className="flex items-center gap-2 bg-blue-50 text-blue-600 hover:bg-blue-100 px-4 py-2.5 rounded-xl text-xs font-bold transition-all"
            >
              <Plus size={16} /> Slayt Ekle
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {slides.map((slide, index) => (
              <div key={slide.id} className="bg-slate-50 border border-slate-200/60 p-6 rounded-2xl relative">
                <button 
                  onClick={() => handleRemoveSlide(slide.id)}
                  className="absolute top-4 right-4 text-red-500 hover:bg-red-50 p-2 rounded-xl transition"
                >
                  <Trash2 size={18} />
                </button>
                <div className="text-xs font-bold text-slate-400 mb-4">Slayt #{index + 1}</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Slider Image Upload */}
                  <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
                    <img src={getImageUrl(slide.image)} alt="Slide Preview" className="w-full h-32 object-cover rounded-lg mb-3 border border-slate-100" />
                    <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold cursor-pointer text-slate-700 transition">
                      <Upload size={14} /> Resim Yükle
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => handleSlideChange(slide.id, 'image', url))} />
                    </label>
                  </div>
                  {/* Slider text fields */}
                  <div className="md:col-span-2 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Mavi Küçük Üst Başlık</label>
                        <input 
                          type="text" 
                          value={slide.subtitle || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'subtitle', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Ana Başlık (Alt satıra geçmek için \n kullanabilirsiniz)</label>
                        <input 
                          type="text" 
                          value={slide.title || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'title', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Açıklama Yazısı</label>
                      <textarea 
                        rows="3"
                        value={slide.desc || ''} 
                        onChange={(e) => handleSlideChange(slide.id, 'desc', e.target.value)}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                      ></textarea>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-slate-200/60 pt-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Buton Linki (Örn: /proje)</label>
                        <input 
                          type="text" 
                          value={slide.link || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'link', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                          placeholder="/proje"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Buton Yazısı (Örn: Projeyi İncele)</label>
                        <input 
                          type="text" 
                          value={slide.link_text || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'link_text', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                          placeholder="Projeyi İncele"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Tanıtım Videosu Linki (Youtube vb.)</label>
                        <input 
                          type="text" 
                          value={slide.video_link || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'video_link', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Video Buton Yazısı</label>
                        <input 
                          type="text" 
                          value={slide.video_text || ''} 
                          onChange={(e) => handleSlideChange(slide.id, 'video_text', e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500" 
                          placeholder="Tanıtım Filmi"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('home_hero_slides', slides)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Slayt Değişikliklerini Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 2. STATS & HOME ABOUT PREVIEW TAB */}
      {activeTab === 'home_about' && (
        <div className="space-y-8">
          {/* Rakamlarla Başlık Ayarları */}
          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/60 mb-6 space-y-4">
            <h3 className="font-bold text-slate-800 text-sm">Sayaç Bölümü Başlık Yazıları</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Küçük Üst Başlık (Örn: Rakamlarla İSİLKOOP)</label>
                <input 
                  type="text" 
                  value={statsSection.subtitle || ''} 
                  onChange={(e) => setStatsSection({ ...statsSection, subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Ana Başlık (Örn: Vizyonumuzu &lt;span class="text-blue-600"&gt;Rakamlara&lt;/span&gt; Döktük)</label>
                <input 
                  type="text" 
                  value={statsSection.title || ''} 
                  onChange={(e) => setStatsSection({ ...statsSection, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Açıklama Yazısı</label>
              <input 
                type="text" 
                value={statsSection.description || ''} 
                onChange={(e) => setStatsSection({ ...statsSection, description: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
              />
            </div>
            <div className="text-right pt-2">
              <button 
                onClick={() => saveSettingKey('home_stats_section', statsSection)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10"
              >
                <Save size={16} /> Başlık Yazılarını Kaydet
              </button>
            </div>
          </div>

          {/* Anasayfa Sayaçları */}
          <div>
            <h2 className="text-xl font-extrabold text-slate-800 mb-4">Anasayfa Rakamlar Sayaçları</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-slate-50 border border-slate-200/60 p-5 rounded-2xl space-y-4">
                  <div className="text-xs font-bold text-slate-400">Sayaç #{index + 1}</div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">İkon Seçin</label>
                    <IconSelect 
                      value={stat.icon} 
                      onChange={(val) => handleStatChange(index, 'icon', val)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Değer (Sayısal)</label>
                    <input 
                      type="number" 
                      value={stat.value} 
                      onChange={(e) => handleStatChange(index, 'value', parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Birim Soneki (Örn: m²)</label>
                    <input 
                      type="text" 
                      value={stat.suffix} 
                      onChange={(e) => handleStatChange(index, 'suffix', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Açıklama Etiketi</label>
                    <input 
                      type="text" 
                      value={stat.label} 
                      onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-right">
              <button 
                onClick={() => saveSettingKey('home_stats', stats)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-xs font-bold transition-all"
              >
                <Save size={16} /> Sayaçları Kaydet
              </button>
            </div>
          </div>

          {/* Anasayfa Parallax Banner */}
          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/60 mb-6 space-y-4">
            <h3 className="font-bold text-slate-800 text-sm">Anasayfa Parallax Banner Ayarları</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Image upload */}
              <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
                <img src={getImageUrl(homeParallax.bg_image)} alt="Parallax Background" className="w-full h-32 object-cover rounded-lg mb-3 border border-slate-100" />
                <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold cursor-pointer text-slate-700 transition">
                  <Upload size={14} /> Resmi Güncelle
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setHomeParallax({ ...homeParallax, bg_image: url }))} />
                </label>
              </div>
              
              {/* Text inputs */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Küçük Üst Başlık (Örn: Geleceğin Vizyonu)</label>
                  <input 
                    type="text" 
                    value={homeParallax.subtitle || ''} 
                    onChange={(e) => setHomeParallax({ ...homeParallax, subtitle: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Ana Başlık (Alt satıra geçmek için \n kullanabilirsiniz)</label>
                  <textarea 
                    rows="2"
                    value={homeParallax.title || ''} 
                    onChange={(e) => setHomeParallax({ ...homeParallax, title: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>
            </div>
            <div className="text-right pt-2">
              <button 
                onClick={() => saveSettingKey('home_parallax', homeParallax)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-3 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10"
              >
                <Save size={16} /> Parallax Ayarlarını Kaydet
              </button>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Anasayfa Hakkımızda Önizleme */}
          {homeAbout.title && (
            <div>
              <h2 className="text-xl font-extrabold text-slate-800 mb-4">Anasayfa Hakkımızda Bölümü</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
                {/* Image upload */}
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
                  <img src={getImageUrl(homeAbout.image)} alt="Home About" className="w-full h-40 object-cover rounded-lg mb-3 border border-slate-100" />
                  <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold cursor-pointer text-slate-700 transition">
                    <Upload size={14} /> Resmi Güncelle
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setHomeAbout({ ...homeAbout, image: url }))} />
                  </label>
                </div>

                {/* Text fields */}
                <div className="md:col-span-2 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Üst Başlığı (Örn: Hakkımızda)</label>
                      <input 
                        type="text" 
                        value={homeAbout.subtitle || ''} 
                        onChange={(e) => setHomeAbout({ ...homeAbout, subtitle: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Başlığı</label>
                      <input 
                        type="text" 
                        value={homeAbout.title || ''} 
                        onChange={(e) => setHomeAbout({ ...homeAbout, title: e.target.value })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1">Kuruluş Yılı (Örn: 2023)</label>
                      <input 
                        type="number" 
                        value={homeAbout.foundation_year || 2023} 
                        onChange={(e) => setHomeAbout({ ...homeAbout, foundation_year: parseInt(e.target.value) || 2023 })}
                        className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Buton Linki</label>
                        <input 
                          type="text" 
                          value={homeAbout.link || ''} 
                          onChange={(e) => setHomeAbout({ ...homeAbout, link: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                          placeholder="/kurumsal/hakkimizda"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Buton Yazısı</label>
                        <input 
                          type="text" 
                          value={homeAbout.link_text || ''} 
                          onChange={(e) => setHomeAbout({ ...homeAbout, link_text: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                          placeholder="Devamını Oku"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1">Açıklama Metni</label>
                    <textarea 
                      rows="4"
                      value={homeAbout.description || ''} 
                      onChange={(e) => setHomeAbout({ ...homeAbout, description: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
                    ></textarea>
                  </div>
                  
                  {/* Points */}
                  <div className="space-y-4 pt-2">
                    <div className="flex justify-between items-center">
                      <div className="text-xs font-bold text-slate-400">Küçük Özellik Kartları</div>
                      <button 
                        type="button"
                        onClick={handleAddHomeAboutPoint}
                        className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition"
                      >
                        <Plus size={14} /> Kart Ekle
                      </button>
                    </div>
                    {homeAbout.points && homeAbout.points.map((point, index) => (
                      <div key={index} className="grid grid-cols-1 sm:grid-cols-4 gap-3 bg-white p-4 rounded-xl border border-slate-200 relative group">
                        <button 
                          type="button"
                          onClick={() => handleRemoveHomeAboutPoint(index)}
                          className="absolute -top-2 -right-2 text-red-500 hover:bg-red-50 p-1 bg-white border border-slate-200 rounded-lg shadow-sm transition opacity-0 group-hover:opacity-100 z-10"
                        >
                          <Trash2 size={14} />
                        </button>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 mb-1">İkon</label>
                          <IconSelect 
                            value={point.icon} 
                            onChange={(val) => handleHomeAboutPointChange(index, 'icon', val)}
                            className="px-3 py-1.5 text-xs"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-400 mb-1">Başlık</label>
                          <input 
                            type="text" 
                            value={point.title || ''} 
                            onChange={(e) => handleHomeAboutPointChange(index, 'title', e.target.value)}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block text-[10px] font-bold text-slate-400 mb-1">Açıklama</label>
                          <input 
                            type="text" 
                            value={point.desc || ''} 
                            onChange={(e) => handleHomeAboutPointChange(index, 'desc', e.target.value)}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button 
                  onClick={() => saveSettingKey('home_about', homeAbout)}
                  disabled={loading}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
                >
                  <Save size={18} /> Bölüm Ayarlarını Kaydet
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3. ABOUT PAGE TAB */}
      {activeTab === 'about_page' && aboutPage.title && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">Hakkımızda Sayfası İçerik Ayarları</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Ana Başlığı</label>
                <input 
                  type="text" 
                  value={aboutPage.title} 
                  onChange={(e) => setAboutPage({ ...aboutPage, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Alt Başlığı</label>
                <input 
                  type="text" 
                  value={aboutPage.subtitle} 
                  onChange={(e) => setAboutPage({ ...aboutPage, subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
            </div>

            {/* Images list */}
            <div className="grid grid-cols-2 gap-4">
              {aboutPage.images && aboutPage.images.map((img, index) => (
                <div key={index} className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-3 bg-white">
                  <img src={getImageUrl(img)} alt="About page img" className="w-full h-24 object-cover rounded-lg mb-2 border border-slate-100" />
                  <label className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                    <Upload size={12} /> Değiştir
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => {
                      const updated = [...aboutPage.images];
                      updated[index] = url;
                      setAboutPage({ ...aboutPage, images: updated });
                    })} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Paragraphs list */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Metin Paragrafları</h3>
              <button 
                onClick={handleAddAboutParagraph}
                className="flex items-center gap-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition"
              >
                <Plus size={14} /> Paragraf Ekle
              </button>
            </div>
            {aboutPage.paragraphs && aboutPage.paragraphs.map((p, index) => (
              <div key={index} className="flex gap-3 items-start">
                <span className="text-xs font-bold text-slate-400 pt-3">#{index + 1}</span>
                <textarea 
                  rows="3"
                  value={p}
                  onChange={(e) => handleAboutParagraphChange(index, e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
                  placeholder="Açıklama paragrafı..."
                ></textarea>
                <button 
                  onClick={() => handleRemoveAboutParagraph(index)}
                  className="text-red-500 hover:bg-red-50 p-2.5 rounded-xl transition mt-1"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('about_page', aboutPage)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Hakkımızda İçeriğini Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 4. MISSION VISION TAB */}
      {activeTab === 'mission_vision' && missionVision.title && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">Misyon & Vizyon Sayfası Ayarları</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Üst Başlığı</label>
                <input 
                  type="text" 
                  value={missionVision.subtitle} 
                  onChange={(e) => setMissionVision({ ...missionVision, subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Başlığı</label>
                <input 
                  type="text" 
                  value={missionVision.title} 
                  onChange={(e) => setMissionVision({ ...missionVision, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Giriş / Tanıtım Metni</label>
              <textarea 
                rows="4"
                value={missionVision.intro} 
                onChange={(e) => setMissionVision({ ...missionVision, intro: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
              ></textarea>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
              <h3 className="text-sm font-bold text-blue-800 mb-3">Vizyonumuz</h3>
              <textarea 
                rows="6"
                value={missionVision.vision} 
                onChange={(e) => setMissionVision({ ...missionVision, vision: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
              ></textarea>
            </div>
            <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100">
              <h3 className="text-sm font-bold text-emerald-800 mb-3">Misyonumuz</h3>
              <textarea 
                rows="6"
                value={missionVision.mission} 
                onChange={(e) => setMissionVision({ ...missionVision, mission: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
              ></textarea>
            </div>
          </div>

          {/* Values */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700">Temel Değerlerimiz</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {missionVision.values && missionVision.values.map((val, index) => (
                <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200">
                  <div className="text-xs font-bold text-slate-400 mb-3">Değer #{index + 1}</div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 mb-1">İkon</label>
                      <IconSelect 
                        value={val.icon} 
                        onChange={(value) => handleMissionValueChange(index, 'icon', value)}
                        className="px-3 py-1.5 text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 mb-1">Başlık</label>
                      <input 
                        type="text" 
                        value={val.title} 
                        onChange={(e) => handleMissionValueChange(index, 'title', e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 mb-1">Açıklama</label>
                      <textarea 
                        rows="3"
                        value={val.desc} 
                        onChange={(e) => handleMissionValueChange(index, 'desc', e.target.value)}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium" 
                      ></textarea>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('mission_vision', missionVision)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Misyon & Vizyon Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 5. QUALITY POLICY TAB */}
      {activeTab === 'quality_policy' && qualityPolicy.title && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">Kalite Politikası İçerikleri</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Politika Üst Başlığı</label>
                <input 
                  type="text" 
                  value={qualityPolicy.subtitle} 
                  onChange={(e) => setQualityPolicy({ ...qualityPolicy, subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Politika Sayfa Başlığı</label>
                <input 
                  type="text" 
                  value={qualityPolicy.title} 
                  onChange={(e) => setQualityPolicy({ ...qualityPolicy, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Giriş Giriş Tanıtım Yazısı</label>
              <textarea 
                rows="4"
                value={qualityPolicy.intro} 
                onChange={(e) => setQualityPolicy({ ...qualityPolicy, intro: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
              ></textarea>
            </div>
          </div>

          {/* Policy List */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700">Kalite Prensipleri Kartları</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {qualityPolicy.policies && qualityPolicy.policies.map((p, index) => (
                <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
                  <div className="text-xs font-bold text-slate-400">Kart #{index + 1}</div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">İkon</label>
                    <IconSelect 
                      value={p.icon} 
                      onChange={(value) => handlePolicyChange(index, 'icon', value)}
                      className="px-3 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Başlık</label>
                    <input 
                      type="text" 
                      value={p.title} 
                      onChange={(e) => handlePolicyChange(index, 'title', e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Açıklama</label>
                    <textarea 
                      rows="3"
                      value={p.desc} 
                      onChange={(e) => handlePolicyChange(index, 'desc', e.target.value)}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-medium" 
                    ></textarea>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('quality_policy', qualityPolicy)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Kalite Ayarlarını Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 6. BOARD MEMBERS TAB */}
      {/* 6. BOARD MEMBERS TAB */}
      {activeTab === 'board_members' && boardMembers && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">Yönetim ve Denetim Kurulu Üyeleri Ayarları</h2>
          
          {/* Coordinator & President */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Coordinator */}
            {boardMembers.generalCoordinator ? (
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 relative">
                <button 
                  onClick={() => setBoardMembers({ ...boardMembers, generalCoordinator: null })}
                  className="absolute top-3 right-3 text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                  title="Üyeyi Sil"
                >
                  <Trash2 size={16} />
                </button>
                <h3 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-4">Genel Koordinatör</h3>
                <div className="flex flex-col items-center mb-4">
                  <Avatar src={boardMembers.generalCoordinator.image} alt="Coordinator" size="md" accent="amber" />
                  <div className="flex gap-1.5 mt-2">
                    <label className="flex items-center gap-1 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                      <Upload size={10} /> Değiştir
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setBoardMembers({
                        ...boardMembers,
                        generalCoordinator: { ...boardMembers.generalCoordinator, image: url }
                      }))} />
                    </label>
                    {boardMembers.generalCoordinator.image && (
                      <button
                        type="button"
                        onClick={() => setBoardMembers({
                          ...boardMembers,
                          generalCoordinator: { ...boardMembers.generalCoordinator, image: null }
                        })}
                        className="flex items-center gap-1 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded-xl text-[10px] font-bold text-red-600 transition"
                      >
                        <Trash2 size={10} /> Sil
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Ad Soyad</label>
                    <input 
                      type="text" 
                      value={boardMembers.generalCoordinator.name} 
                      onChange={(e) => setBoardMembers({
                        ...boardMembers,
                        generalCoordinator: { ...boardMembers.generalCoordinator, name: e.target.value }
                      })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-6 rounded-3xl border border-dashed border-slate-300/80 flex flex-col items-center justify-center min-h-[250px] gap-2">
                <span className="text-xs text-slate-400 font-bold">Genel Koordinatör Bulunmuyor</span>
                <button 
                  onClick={() => setBoardMembers({
                    ...boardMembers,
                    generalCoordinator: { role: 'Genel Koordinatör', name: '', image: null }
                  })}
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                >
                  <Plus size={14} /> Üye Ekle
                </button>
              </div>
            )}

            {/* President */}
            {boardMembers.president ? (
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 relative">
                <button 
                  onClick={() => setBoardMembers({ ...boardMembers, president: null })}
                  className="absolute top-3 right-3 text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                  title="Üyeyi Sil"
                >
                  <Trash2 size={16} />
                </button>
                <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4">Yönetim Kurulu Başkanı</h3>
                <div className="flex flex-col items-center mb-4">
                  <Avatar src={boardMembers.president.image} alt="President" size="md" accent="blue" />
                  <div className="flex gap-1.5 mt-2">
                    <label className="flex items-center gap-1 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                      <Upload size={10} /> Değiştir
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setBoardMembers({
                        ...boardMembers,
                        president: { ...boardMembers.president, image: url }
                      }))} />
                    </label>
                    {boardMembers.president.image && (
                      <button
                        type="button"
                        onClick={() => setBoardMembers({
                          ...boardMembers,
                          president: { ...boardMembers.president, image: null }
                        })}
                        className="flex items-center gap-1 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded-xl text-[10px] font-bold text-red-600 transition"
                      >
                        <Trash2 size={10} /> Sil
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Ad Soyad</label>
                    <input 
                      type="text" 
                      value={boardMembers.president.name} 
                      onChange={(e) => setBoardMembers({
                        ...boardMembers,
                        president: { ...boardMembers.president, name: e.target.value }
                      })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-6 rounded-3xl border border-dashed border-slate-300/80 flex flex-col items-center justify-center min-h-[250px] gap-2">
                <span className="text-xs text-slate-400 font-bold">Yönetim Kurulu Başkanı Bulunmuyor</span>
                <button 
                  onClick={() => setBoardMembers({
                    ...boardMembers,
                    president: { role: 'Yönetim Kurulu Başkanı', name: '', image: null }
                  })}
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                >
                  <Plus size={14} /> Üye Ekle
                </button>
              </div>
            )}

            {/* Vice Chairman */}
            {boardMembers.viceChairman ? (
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 relative">
                <button 
                  onClick={() => setBoardMembers({ ...boardMembers, viceChairman: null })}
                  className="absolute top-3 right-3 text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-xl transition-all"
                  title="Üyeyi Sil"
                >
                  <Trash2 size={16} />
                </button>
                <h3 className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4">Başkan Yardımcısı</h3>
                <div className="flex flex-col items-center mb-4">
                  <Avatar src={boardMembers.viceChairman.image} alt="Vice President" size="md" accent="blue" />
                  <div className="flex gap-1.5 mt-2">
                    <label className="flex items-center gap-1 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                      <Upload size={10} /> Değiştir
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setBoardMembers({
                        ...boardMembers,
                        viceChairman: { ...boardMembers.viceChairman, image: url }
                      }))} />
                    </label>
                    {boardMembers.viceChairman.image && (
                      <button
                        type="button"
                        onClick={() => setBoardMembers({
                          ...boardMembers,
                          viceChairman: { ...boardMembers.viceChairman, image: null }
                        })}
                        className="flex items-center gap-1 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded-xl text-[10px] font-bold text-red-600 transition"
                      >
                        <Trash2 size={10} /> Sil
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Ad Soyad</label>
                    <input 
                      type="text" 
                      value={boardMembers.viceChairman.name} 
                      onChange={(e) => setBoardMembers({
                        ...boardMembers,
                        viceChairman: { ...boardMembers.viceChairman, name: e.target.value }
                      })}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold" 
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 p-6 rounded-3xl border border-dashed border-slate-300/80 flex flex-col items-center justify-center min-h-[250px] gap-2">
                <span className="text-xs text-slate-400 font-bold">Başkan Yardımcısı Bulunmuyor</span>
                <button 
                  onClick={() => setBoardMembers({
                    ...boardMembers,
                    viceChairman: { role: 'Yönetim Kurulu Başkan Yardımcısı', name: '', image: null }
                  })}
                  className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-bold transition-all"
                >
                  <Plus size={14} /> Üye Ekle
                </button>
              </div>
            )}
          </div>

          <hr className="border-slate-100" />

          {/* Board Members list */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Yönetim Kurulu Üyeleri</h3>
              <button 
                onClick={handleAddBoardMember}
                className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition"
              >
                <Plus size={14} /> Üye Ekle
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {boardMembers.boardMembers && boardMembers.boardMembers.map((member, index) => (
                <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 relative">
                  <button 
                    onClick={() => handleRemoveBoardMember(index)}
                    className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="flex flex-col items-center mb-4">
                    <Avatar src={member.image} alt={member.name} size="sm" accent="slate" />
                    <div className="flex gap-1.5 mt-2">
                      <label className="flex items-center gap-1 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                        <Upload size={10} /> Değiştir
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => handleBoardMemberChange(index, 'image', url))} />
                      </label>
                      {member.image && (
                        <button
                          type="button"
                          onClick={() => handleBoardMemberChange(index, 'image', null)}
                          className="flex items-center gap-1 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded-xl text-[10px] font-bold text-red-600 transition"
                        >
                          <Trash2 size={10} /> Sil
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400">Görev / Rol</label>
                      <input 
                        type="text" 
                        value={member.role} 
                        onChange={(e) => handleBoardMemberChange(index, 'role', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-semibold" 
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400">Ad Soyad</label>
                      <input 
                        type="text" 
                        value={member.name} 
                        onChange={(e) => handleBoardMemberChange(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-semibold" 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Audit Committee list */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">Denetim Kurulu Üyeleri</h3>
              <button 
                onClick={handleAddAuditMember}
                className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-3 py-1.5 rounded-lg text-xs font-bold transition"
              >
                <Plus size={14} /> Denetçi Ekle
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {boardMembers.auditCommittee && boardMembers.auditCommittee.map((member, index) => (
                <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 relative">
                  <button 
                    onClick={() => handleRemoveAuditMember(index)}
                    className="absolute top-2 right-2 text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition"
                  >
                    <Trash2 size={16} />
                  </button>
                  <div className="flex flex-col items-center mb-4">
                    <Avatar src={member.image} alt={member.name} size="sm" accent="emerald" />
                    <div className="flex gap-1.5 mt-2">
                      <label className="flex items-center gap-1 bg-white hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-xl text-[10px] font-bold cursor-pointer text-slate-700 transition">
                        <Upload size={10} /> Değiştir
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => handleAuditMemberChange(index, 'image', url))} />
                      </label>
                      {member.image && (
                        <button
                          type="button"
                          onClick={() => handleAuditMemberChange(index, 'image', null)}
                          className="flex items-center gap-1 bg-red-50 hover:bg-red-100 border border-red-200 px-2.5 py-1 rounded-xl text-[10px] font-bold text-red-600 transition"
                        >
                          <Trash2 size={10} /> Sil
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400">Görev / Rol</label>
                      <input 
                        type="text" 
                        value={member.role} 
                        onChange={(e) => handleAuditMemberChange(index, 'role', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-semibold" 
                      />
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-slate-400">Ad Soyad</label>
                      <input 
                        type="text" 
                        value={member.name} 
                        onChange={(e) => handleAuditMemberChange(index, 'name', e.target.value)}
                        className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-semibold" 
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('board_members', boardMembers)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Yönetim Kadrosunu Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 7. PROJECT DETAIL TAB */}
      {activeTab === 'project_detail' && projectDetail.stats && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">Proje Detay Sayfası Ayarları</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            {/* Hero Image upload */}
            <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
              <img src={getImageUrl(projectDetail.hero_image)} alt="Project Hero" className="w-full h-32 object-cover rounded-lg mb-3 border border-slate-100" />
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold cursor-pointer text-slate-700 transition">
                <Upload size={14} /> Manşeti Değiştir
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setProjectDetail({ ...projectDetail, hero_image: url }))} />
              </label>
            </div>

            <div className="md:col-span-2 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Küçük Üst Başlık</label>
                <input 
                  type="text" 
                  value={projectDetail.hero_subtitle} 
                  onChange={(e) => setProjectDetail({ ...projectDetail, hero_subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Ana Başlık</label>
                <input 
                  type="text" 
                  value={projectDetail.hero_title} 
                  onChange={(e) => setProjectDetail({ ...projectDetail, hero_title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Giriş Tanıtım Yazısı</label>
                <textarea 
                  rows="3"
                  value={projectDetail.hero_desc} 
                  onChange={(e) => setProjectDetail({ ...projectDetail, hero_desc: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none"
                ></textarea>
              </div>
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Project Stats */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700">Proje Rakamları (4 Kart)</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {projectDetail.stats.map((s, index) => (
                <div key={index} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2.5">
                  <div className="text-[10px] font-bold text-slate-400">Kart #{index + 1}</div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">İkon</label>
                    <IconSelect 
                      value={s.icon} 
                      onChange={(value) => handleProjStatChange(index, 'icon', value)}
                      className="px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">Değer</label>
                    <input 
                      type="number" 
                      value={s.value} 
                      onChange={(e) => handleProjStatChange(index, 'value', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs" 
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">Sonek (Birim)</label>
                    <input 
                      type="text" 
                      value={s.suffix} 
                      onChange={(e) => handleProjStatChange(index, 'suffix', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs" 
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">Açıklama</label>
                    <input 
                      type="text" 
                      value={s.label} 
                      onChange={(e) => handleProjStatChange(index, 'label', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs" 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Project Features */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700">Üretime Değer Katan Özellikler (6 Kart)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projectDetail.features && projectDetail.features.map((feat, index) => (
                <div key={index} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2.5">
                  <div className="text-[10px] font-bold text-slate-400">Özellik #{index + 1}</div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">İkon</label>
                    <IconSelect 
                      value={feat.icon} 
                      onChange={(value) => handleFeatureChange(index, 'icon', value)}
                      className="px-2 py-1.5 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">Başlık</label>
                    <input 
                      type="text" 
                      value={feat.title} 
                      onChange={(e) => handleFeatureChange(index, 'title', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs" 
                    />
                  </div>
                  <div>
                    <label className="block text-[9px] font-bold text-slate-400">Açıklama</label>
                    <textarea 
                      rows="3"
                      value={feat.desc} 
                      onChange={(e) => handleFeatureChange(index, 'desc', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-300 rounded text-xs" 
                    ></textarea>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Vaziyet planları / krokiler */}
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-slate-700">Vaziyet Planları & Krokiler</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {projectDetail.krokiler && projectDetail.krokiler.map((kroki, index) => (
                <div key={index} className="bg-slate-50 p-5 rounded-2xl border border-slate-200 flex flex-col md:flex-row gap-4 items-center">
                  <div className="flex flex-col items-center border border-slate-200 rounded-xl p-3 bg-white w-48 shrink-0">
                    <img src={getImageUrl(kroki.src)} alt="Kroki" className="h-24 object-contain rounded mb-2" />
                    <label className="flex items-center gap-1 bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded text-[10px] font-bold cursor-pointer text-slate-700 transition">
                      <Upload size={10} /> Değiştir
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => handleKrokiChange(index, 'src', url))} />
                    </label>
                  </div>
                  <div className="w-full">
                    <label className="block text-xs font-bold text-slate-500 mb-1">Kroki Açıklama Başlığı</label>
                    <input 
                      type="text" 
                      value={kroki.label} 
                      onChange={(e) => handleKrokiChange(index, 'label', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-semibold" 
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <hr className="border-slate-100" />

          {/* Project Visual Gallery */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-sm font-bold text-slate-700">3D Proje Renders Galerisi</h3>
              <label className="flex items-center gap-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition">
                <Plus size={14} /> Render Görseli Ekle
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => handleAddGalleryRender(url))} />
              </label>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {projectDetail.gallery && projectDetail.gallery.map((src, index) => (
                <div key={index} className="relative border border-slate-200 rounded-2xl overflow-hidden aspect-square group bg-slate-100 shadow-sm">
                  <img src={getImageUrl(src)} alt="Gallery Item" className="w-full h-full object-cover" />
                  <button 
                    onClick={() => handleRemoveGalleryRender(index)}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg shadow transition opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('project_detail', projectDetail)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Proje Ayarlarını Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 8. HR PAGE TAB */}
      {activeTab === 'hr_page' && hrPage.title && (
        <div className="space-y-8">
          <h2 className="text-xl font-extrabold text-slate-800">İnsan Kaynakları Sayfası Ayarları</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Ana Başlığı</label>
                <input 
                  type="text" 
                  value={hrPage.title || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Sayfa Üst/Alt Başlığı</label>
                <input 
                  type="text" 
                  value={hrPage.subtitle || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, subtitle: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Giriş / Tanıtım Metni</label>
              <textarea 
                rows="4"
                value={hrPage.description || ''} 
                onChange={(e) => setHrPage({ ...hrPage, description: e.target.value })}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Bizimle Çalışmak Bölümü</h3>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Başlığı</label>
                <input 
                  type="text" 
                  value={hrPage.about_title || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, about_title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Açıklama Metni</label>
                <textarea 
                  rows="4"
                  value={hrPage.about_description || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, about_description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-bold text-slate-700">Açık Pozisyonlar Bölümü</h3>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Bölüm Başlığı</label>
                <input 
                  type="text" 
                  value={hrPage.positions_title || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, positions_title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Genel Açıklama Metni (Liste boşken gösterilir)</label>
                <textarea 
                  rows="4"
                  value={hrPage.positions_description || ''} 
                  onChange={(e) => setHrPage({ ...hrPage, positions_description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Benefits list */}
            <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold text-slate-700">Neler Sunuyoruz? Özellikleri</h3>
                <button 
                  type="button"
                  onClick={handleAddBenefit}
                  className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg text-xs font-bold transition"
                >
                  <Plus size={14} /> Özellik Ekle
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {hrPage.benefits && hrPage.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <span className="text-xs font-bold text-slate-400 w-5">#{index + 1}</span>
                    <input 
                      type="text" 
                      value={benefit || ''} 
                      onChange={(e) => handleBenefitChange(index, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      placeholder="Sunulan avantaj/eğitim vb..."
                    />
                    <button 
                      type="button"
                      onClick={() => handleRemoveBenefit(index)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Positions list */}
            <div className="space-y-4 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold text-slate-700">Aktif Açık Pozisyonlar Listesi</h3>
                <button 
                  type="button"
                  onClick={handleAddPosition}
                  className="flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 px-2.5 py-1.5 rounded-lg text-xs font-bold transition"
                >
                  <Plus size={14} /> Pozisyon Ekle
                </button>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {hrPage.positions && hrPage.positions.map((pos, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <span className="text-xs font-bold text-slate-400 w-5">#{index + 1}</span>
                    <input 
                      type="text" 
                      value={pos || ''} 
                      onChange={(e) => handlePositionChange(index, e.target.value)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                      placeholder="Açık pozisyon adı..."
                    />
                    <button 
                      type="button"
                      onClick={() => handleRemovePosition(index)}
                      className="text-red-500 hover:bg-red-50 p-2 rounded-xl transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {(!hrPage.positions || hrPage.positions.length === 0) && (
                  <div className="text-xs text-slate-400 italic text-center py-4">Açık pozisyon listesi boş. Sitede genel açıklama yazısı gösterilecek.</div>
                )}
              </div>
            </div>
          </div>

        </div>
      )}

      {/* 9. CONTACT SETTINGS TAB */}
      {activeTab === 'contact_settings' && contactSettings && (
        <div className="space-y-6">
          <h2 className="text-xl font-extrabold text-slate-800">İletişim Bilgileri Ayarları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Telefon Numarası</label>
                <input 
                  type="text" 
                  value={contactSettings.phone || ''} 
                  onChange={(e) => setContactSettings({ ...contactSettings, phone: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">E-Posta Adresi</label>
                <input 
                  type="email" 
                  value={contactSettings.email || ''} 
                  onChange={(e) => setContactSettings({ ...contactSettings, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Harita iframe URL (src değeri)</label>
                <textarea 
                  rows="3"
                  value={contactSettings.map_iframe || ''} 
                  onChange={(e) => setContactSettings({ ...contactSettings, map_iframe: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-xs" 
                  placeholder="https://maps.google.com/maps?..."
                />
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Merkez Ofis Adresi</label>
                <textarea 
                  rows="3"
                  value={contactSettings.address_office || ''} 
                  onChange={(e) => setContactSettings({ ...contactSettings, address_office: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Şantiye Adresi</label>
                <textarea 
                  rows="3"
                  value={contactSettings.address_site || ''} 
                  onChange={(e) => setContactSettings({ ...contactSettings, address_site: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('contact_settings', contactSettings)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> İletişim Bilgilerini Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 10. FOOTER SETTINGS TAB */}
      {activeTab === 'footer_settings' && footerSettings && (
        <div className="space-y-6">
          <h2 className="text-xl font-extrabold text-slate-800">Footer (Alt Bilgi) Ayarları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-6 rounded-2xl border border-slate-200/60">
            <div className="space-y-4">
              {/* Logo upload */}
              <div className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl p-4 bg-white">
                {footerSettings.logo_url ? (
                  <img src={getImageUrl(footerSettings.logo_url)} alt="Footer Logo" className="h-12 object-contain mb-3 bg-[#0f172a] p-2 rounded" />
                ) : (
                  <div className="h-12 flex items-center justify-center text-slate-400 text-xs italic mb-3">Varsayılan Logo Kullanılıyor</div>
                )}
                <label className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-xs font-bold cursor-pointer text-slate-700 transition">
                  <Upload size={14} /> Logo Yükle
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, (url) => setFooterSettings({ ...footerSettings, logo_url: url }))} />
                </label>
                {footerSettings.logo_url && (
                  <button 
                    type="button" 
                    onClick={() => setFooterSettings({ ...footerSettings, logo_url: '' })}
                    className="text-red-500 hover:underline text-xs mt-2 font-bold"
                  >
                    Varsayılana Sıfırla
                  </button>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Kısa Tanıtım Yazısı</label>
                <textarea 
                  rows="3"
                  value={footerSettings.short_desc || ''} 
                  onChange={(e) => setFooterSettings({ ...footerSettings, short_desc: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Copyright Metni</label>
                  <input 
                    type="text" 
                    value={footerSettings.copyright || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, copyright: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Tasarımcı/Geliştirici Metni</label>
                  <input 
                    type="text" 
                    value={footerSettings.designed_by || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, designed_by: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Instagram Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.instagram || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, instagram: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Facebook Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.facebook || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, facebook: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Twitter / X Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.twitter || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, twitter: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">LinkedIn Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.linkedin || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, linkedin: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">YouTube Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.youtube || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, youtube: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Pinterest Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.pinterest || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, pinterest: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 mb-1">TikTok Linki</label>
                  <input 
                    type="text" 
                    value={footerSettings.tiktok || ''} 
                    onChange={(e) => setFooterSettings({ ...footerSettings, tiktok: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 text-[10px] text-blue-800 font-bold mb-2">
                * Footer İletişim Bilgileri alanları boş bırakılırsa, yan taraftaki İletişim Sayfası Bilgileri varsayılan olarak kullanılır.
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Footer Telefon Numarası (Özel)</label>
                <input 
                  type="text" 
                  value={footerSettings.footer_phone || ''} 
                  onChange={(e) => setFooterSettings({ ...footerSettings, footer_phone: e.target.value })}
                  placeholder="İletişim ayarlarındaki tel kullanılacak"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Footer E-Posta Adresi (Özel)</label>
                <input 
                  type="email" 
                  value={footerSettings.footer_email || ''} 
                  onChange={(e) => setFooterSettings({ ...footerSettings, footer_email: e.target.value })}
                  placeholder="İletişim ayarlarındaki e-posta kullanılacak"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Footer Merkez Ofis Adresi (Özel)</label>
                <textarea 
                  rows="2"
                  value={footerSettings.footer_address_office || ''} 
                  onChange={(e) => setFooterSettings({ ...footerSettings, footer_address_office: e.target.value })}
                  placeholder="İletişim ayarlarındaki ofis adresi kullanılacak"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Footer Şantiye Adresi (Özel)</label>
                <textarea 
                  rows="2"
                  value={footerSettings.footer_address_site || ''} 
                  onChange={(e) => setFooterSettings({ ...footerSettings, footer_address_site: e.target.value })}
                  placeholder="İletişim ayarlarındaki şantiye adresi kullanılacak"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                />
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-100">
            <button 
              onClick={() => saveSettingKey('footer_settings', footerSettings)}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
            >
              <Save size={18} /> Footer Ayarlarını Kaydet
            </button>
          </div>
        </div>
      )}

      {/* 11. SMTP & SECURITY TAB */}
      {activeTab === 'smtp_settings' && (
        <div className="space-y-8">
          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/60 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 mb-1">SMTP E-Posta Ayarları</h2>
              <p className="text-xs text-slate-500">Kariyer formları ve İletişim sayfasından gelen mesajların e-posta ile iletilmesini sağlayan sunucu ayarları.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">SMTP Sunucusu (Host)</label>
                  <input 
                    type="text" 
                    value={smtpSettings.host || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                    placeholder="mail.example.com veya smtp.gmail.com"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">SMTP Portu</label>
                  <input 
                    type="text" 
                    value={smtpSettings.port || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, port: e.target.value })}
                    placeholder="587 veya 465"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">E-Posta Kullanıcı Adı (Email)</label>
                  <input 
                    type="text" 
                    value={smtpSettings.username || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                    placeholder="info@example.com"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">E-Posta Şifresi</label>
                  <input 
                    type="password" 
                    value={smtpSettings.password || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                    placeholder="••••••••••••"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Güvenlik Protokolü (Encryption)</label>
                  <select 
                    value={smtpSettings.encryption || 'tls'} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, encryption: e.target.value })}
                    className="w-full bg-white px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="none">Yok (None)</option>
                    <option value="ssl">SSL (Port 465 için önerilir)</option>
                    <option value="tls">STARTTLS / TLS (Port 587 için önerilir)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Gönderici E-Posta (From Email)</label>
                  <input 
                    type="email" 
                    value={smtpSettings.from_email || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                    placeholder="info@example.com"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Gönderici Adı (From Name)</label>
                  <input 
                    type="text" 
                    value={smtpSettings.from_name || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                    placeholder="İSİLKOOP"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Maillerin Gideceği Adres (Receive Email)</label>
                  <input 
                    type="email" 
                    value={smtpSettings.receive_email || ''} 
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, receive_email: e.target.value })}
                    placeholder="form-alici@example.com"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 text-right">
              <button 
                onClick={() => saveSettingKey('smtp_settings', smtpSettings)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
              >
                <Save size={18} /> SMTP Ayarlarını Kaydet
              </button>
            </div>
          </div>

          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/60 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 mb-1">Google reCAPTCHA v2 Güvenlik Ayarları</h2>
              <p className="text-xs text-slate-500">İletişim ve kariyer formlarında robot gönderimleri ve spamları engellemek için Google reCAPTCHA v2 (Ben robot değilim) doğrulaması ekleyin.</p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input 
                  type="checkbox" 
                  id="recaptcha_enabled"
                  checked={recaptchaSettings.enabled === true || recaptchaSettings.enabled === 'true' || recaptchaSettings.enabled === 1} 
                  onChange={(e) => setRecaptchaSettings({ ...recaptchaSettings, enabled: e.target.checked })}
                  className="w-5 h-5 border border-slate-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="recaptcha_enabled" className="text-sm font-bold text-slate-700 cursor-pointer">
                  reCAPTCHA Korumasını Etkinleştir
                </label>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Site Anahtarı (Site Key)</label>
                  <input 
                    type="text" 
                    value={recaptchaSettings.site_key || ''} 
                    onChange={(e) => setRecaptchaSettings({ ...recaptchaSettings, site_key: e.target.value })}
                    placeholder="Google'dan alınan Site Key"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Gizli Anahtar (Secret Key)</label>
                  <input 
                    type="password" 
                    value={recaptchaSettings.secret_key || ''} 
                    onChange={(e) => setRecaptchaSettings({ ...recaptchaSettings, secret_key: e.target.value })}
                    placeholder="Google'dan alınan Secret Key"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500" 
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 text-right">
              <button 
                onClick={() => saveSettingKey('recaptcha_settings', recaptchaSettings)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
              >
                <Save size={18} /> reCAPTCHA Ayarlarını Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 12. HEADER SCRIPTS TAB */}
      {activeTab === 'header_scripts' && (
        <div className="space-y-6">
          <div className="bg-slate-50 p-6 rounded-3xl border border-slate-200/60 space-y-6">
            <div>
              <h2 className="text-lg font-bold text-slate-800 mb-1">Header Kod Ekleme (Google Analytics / Pixel)</h2>
              <p className="text-xs text-slate-500">Sitenin &lt;head&gt; etiketinin içerisine eklenecek özel kodları (Google Analytics, Google Tag Manager, Facebook Pixel vb.) buraya yapıştırabilirsiniz.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-2">Eklemek İstediğiniz Kodlar (HTML/Script)</label>
                <textarea 
                  rows="12"
                  value={headerScripts || ''} 
                  onChange={(e) => setHeaderScripts(e.target.value)}
                  placeholder="<!-- Google tag (gtag.js) -->&#10;<script async src='https://www.googletagmanager.com/gtag/js?id=...'></script>&#10;<script>&#10;  window.dataLayer = window.dataLayer || [];&#10;  ...&#10;</script>"
                  className="w-full px-4 py-3 border border-slate-300 rounded-2xl text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-900 text-slate-100 placeholder-slate-500 leading-relaxed" 
                />
              </div>
            </div>

            <div className="pt-4 text-right">
              <button 
                onClick={() => saveSettingKey('header_scripts', headerScripts)}
                disabled={loading}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 rounded-xl text-sm font-bold shadow-lg shadow-blue-600/10 transition-all disabled:opacity-50"
              >
                <Save size={18} /> Kodları Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;
